import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useGetOrdersQuery } from "src/api/services/ordersApi";
import OrdersTable from "./components/OrdersTable";
import { useCallback, useMemo } from "react";
import {
  BILLING_MODE,
  FLAG,
  ID,
  LIMIT,
  ORDER,
  ORDER_BY,
  PAGE,
  PATIENT_ID,
  PHARMACY,
  PRODUCT,
  STATUS,
} from "src/constants/searchParams";
import { useSearchParams } from "react-router-dom";
import { toNumber } from "src/utils/toNumber";
import { filters } from "./utils/filters";
import TableFilter from "src/components/TableFilter";
import {
  Box,
  Button,
  FormControlLabel,
  LinearProgress,
  Switch,
} from "@mui/material";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { theme } from "src/assets/theme";
import { useOrdersModal } from "src/context/OrdersModalProvider/useOrdersModal";
import SearchById from "src/shared/SearchById";
import { useGetPrescriptionsQuery } from "src/api/services/prescriptionsApi";
import PendingPrescriptions from "src/components/PendingPrescriptions";
import { useManageModal } from "src/hooks/useManageModal";

interface Props {
  count: number;
}

const OrdersActions = ({ count }: Props) => {
  const {
    updateActions: { handleOpen },
  } = useOrdersModal();

  return (
    <Stack
      width="100%"
      height={60}
      direction="row"
      bgcolor={theme.palette.teal[100]}
      justifyContent="space-between"
      alignItems="center"
      padding={"0 16px"}
    >
      <Typography>Selected: {count}</Typography>
      <Button size="small" variant="secondary" onClick={handleOpen}>
        Update
      </Button>
    </Stack>
  );
};

const Orders = () => {
  const { selected } = useSelection();
  const [params, setSearchParams] = useSearchParams();
  const page = useMemo(() => toNumber(params.get(PAGE)), [params]);
  const limit = useMemo(() => toNumber(params.get(LIMIT)), [params]);
  const { data: pendingPrescriptions = [] } = useGetPrescriptionsQuery({
    status: "created",
  });
  const [open, { handleClose, handleOpen }] = useManageModal();

  const handleSwitch = useCallback(() => {
    if (params.get(FLAG) === "delayed") {
      params.delete(FLAG);
    } else {
      params.set(FLAG, "delayed");
    }

    setSearchParams(params);
  }, [params, setSearchParams]);

  const from = useMemo(() => {
    return page * limit;
  }, [limit, page]);

  const to = useMemo(() => {
    return from + limit - 1;
  }, [from, limit]);

  const queryParams = useMemo(() => {
    if (params.get(ID) != null) {
      return {
        id: params.get(ID) ?? undefined,
        from,
        to,
      };
    }

    return {
      id: params.get(ID) ?? undefined,
      order_by: params.get(ORDER_BY) ?? undefined,
      ascending: params.get(ORDER) === "asc" ? true : false,
      billing_model: params.get(BILLING_MODE) ?? undefined,
      pharmacy: params.get(PHARMACY) ?? undefined,
      product: params.get(PRODUCT) ?? undefined,
      status: params.get(STATUS) ?? undefined,
      patient_id: params.get("pid") ?? undefined,
      is_delayed: params.get(FLAG) === "delayed" ? true : undefined,
      from,
      to,
    };
  }, [from, params, to]);

  const { data, isFetching } = useGetOrdersQuery(queryParams);

  const { orders, count } = useMemo(() => {
    if (data == null) return { orders: [], count: 0 };

    return { orders: data.data, count: data.count };
  }, [data]);

  return (
    <>
      <Stack gap="32px">
        <Stack gap={2}>
          <Typography variant="h4" fontWeight={600}>
            Orders
          </Typography>
          {pendingPrescriptions.length > 0 ? (
            <Button sx={{ width: "fit-content" }} onClick={handleOpen}>
              <Typography
                variant="h6"
                fontWeight={600}
              >{`Pending Prescriptions (${pendingPrescriptions.length})`}</Typography>
            </Button>
          ) : null}
        </Stack>
        <Stack
          direction="row"
          gap="16px"
          height={60}
          width="100%"
          alignItems="center"
        >
          {selected.length > 0 ? (
            <OrdersActions count={selected.length} />
          ) : (
            <>
              {filters.map((filter) => (
                <TableFilter key={filter.name} current={filter} />
              ))}
              <SearchById name={PATIENT_ID} label="Patient ID" />
              <SearchById name={ID} label="Order ID" />
              <FormControlLabel
                control={
                  <Switch
                    checked={params.get(FLAG) === "delayed"}
                    onChange={handleSwitch}
                  />
                }
                label="Delayed"
              />
            </>
          )}
        </Stack>
        <Stack gap={0}>
          <Box height={4}>
            {isFetching ? <LinearProgress color="secondary" /> : null}
          </Box>
          <OrdersTable orders={orders} total={count} />
        </Stack>
      </Stack>
      <PendingPrescriptions
        open={open}
        prescriptions={pendingPrescriptions}
        onClose={handleClose}
      />
    </>
  );
};

export default Orders;
